import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { forkJoin } from 'rxjs';
import { saveAs } from 'file-saver';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { SitesService, SiteInstances } from '../../shared/services/sites.service';
import { UserContextService } from '../../shared/services/user-context.service';
import { UserSessionService } from '../../shared/services/user-session.service';
import { SiteDetailsService, SiteEntity, SiteUser, UserSiteAccess, EntityDetailSummary } from '../../shared/services/site-details.service';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { UserDetailModalComponent, UserDetailData } from '../../shared/components/user-detail-modal/user-detail-modal.component';
import { EntityDetailModalComponent, EntityDetailData } from '../../shared/components/entity-detail-modal/entity-detail-modal.component';
import { AddUserModalComponent } from '../../users/add-user-modal.component';
import { Utils } from '../../shared/utilities/utils';

@Component({
  selector: 'app-sites-table',
  templateUrl: './sites-table.component.html',
  styleUrls: ['./sites-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AngularSvgIconModule, UserDetailModalComponent, EntityDetailModalComponent, AddUserModalComponent],
  host: {
    '(document:keydown)': 'onKeydown($event)'
  }
})
export class SitesTableComponent {
  private readonly sitesService = inject(SitesService);
  private readonly userContextService = inject(UserContextService);
  private readonly userSessionService = inject(UserSessionService);
  private readonly siteDetailsService = inject(SiteDetailsService);
  private readonly destroyRef = inject(DestroyRef);

  // Signals for reactive state management
  readonly siteData = signal<SiteInstances[]>([]);
  readonly error = signal<string | null>(null);
  readonly isAdmin = computed(() => this.userSessionService.userSession?.isAdmin ?? false);

  // Search & sort for sites table
  readonly siteSearchTerm = signal('');
  readonly sortField = signal<'name' | 'userCount'>('name');
  readonly sortAscending = signal(true);

  readonly filteredSortedSiteData = computed(() => {
    const term = this.siteSearchTerm().toLowerCase().trim();
    let data = this.siteData();

    if (term) {
      data = data.filter(site => site.name.toLowerCase().includes(term));
    }

    const field = this.sortField();
    const asc = this.sortAscending() ? 1 : -1;
    return [...data].sort((a, b) => {
      if (field === 'name') {
        return a.name.localeCompare(b.name) * asc;
      }
      return ((a.userCount ?? 0) - (b.userCount ?? 0)) * asc;
    });
  });

  // Search for users in detail panel
  readonly userSearchTerm = signal('');
  readonly filteredSiteUsers = computed(() => {
    const term = this.userSearchTerm().toLowerCase().trim();
    const users = this.siteUsers();
    if (!term) {
      return users;
    }
    return users.filter(u =>
      u.email.toLowerCase().includes(term) ||
      (u.displayName ?? '').toLowerCase().includes(term) ||
      u.name.toLowerCase().includes(term)
    );
  });
  readonly isDetailPanelOpen = signal(false);
  readonly selectedSite = signal<SiteInstances | null>(null);
  readonly isModalExpanded = signal(false);
  readonly isUserDetailOpen = signal(false);
  readonly selectedUserData = signal<UserDetailData | null>(null);
  readonly isEntityDetailOpen = signal(false);
  readonly selectedEntityData = signal<EntityDetailData | null>(null);
  readonly isAddUserModalOpen = signal(false);

  // Tab functionality
  readonly activeTab = signal<'entities' | 'users'>('entities');

  // Site detail panel data
  readonly userCount = signal(0);
  readonly siteEntities = signal<SiteEntity[]>([]);
  readonly siteUsers = signal<SiteUser[]>([]);

  constructor() {
    this.loadSiteData();
  }

  /**
   * Load site details for the slide-in panel
   * Fetches real data from the backend API
   */
  loadSiteDetails(siteId: string): void {
    const site = this.siteData().find(s => s.id === siteId) || null;
    this.selectedSite.set(site);
    if (!site) {
      return;
    }

    // Load site entities and users in parallel
    forkJoin({
      entities: this.siteDetailsService.getSiteEntities(site.id),
      users: this.siteDetailsService.getSiteUsers(site.id)
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.siteEntities.set(response.entities || []);
          this.siteUsers.set(response.users || []);
          this.userCount.set(this.siteUsers().length);
          this.isDetailPanelOpen.set(true);
          document.body.classList.add('detail-panel-open');
        },
        error: (error) => {
          console.error('Error loading site details:', error);
          this.siteEntities.set([]);
          this.siteUsers.set([]);
          this.userCount.set(0);
        }
      });
  }

  closeDetailPanel(): void {
    this.isDetailPanelOpen.set(false);
    this.isModalExpanded.set(false);
    document.body.classList.remove('detail-panel-open');
  }

  /**
   * Get user count for a specific site
   * This is a placeholder method - in a real app, you'd fetch this from the API
   * or maintain a cache of user counts per site
   */
  getSiteUserCount(_siteId: string): number {
    // Placeholder implementation - return 0 if no data available
    // In a real implementation, you would fetch this from your API or maintain a cache
    return 0;
  }

  toggleModalSize(): void {
    this.isModalExpanded.update(v => !v);
  }

  // Tab functionality methods
  setActiveTab(tab: 'entities' | 'users'): void {
    if (tab === 'entities' || tab === 'users') {
      this.activeTab.set(tab);
    }
  }

  isActiveTab(tab: 'entities' | 'users'): boolean {
    return this.activeTab() === tab;
  }

  // Admin action methods
  addUser(): void {
    this.isAddUserModalOpen.set(true);
  }

  closeAddUserModal(): void {
    this.isAddUserModalOpen.set(false);
  }

  onUserAdded(): void {
    // Reload site details to refresh the user list
    const site = this.selectedSite();
    if (site) {
      this.loadSiteDetails(site.id);
    }
  }

  viewEntity(entity: SiteEntity): void {
    // Get tenant context for API call
    const site = this.selectedSite();
    const siteKey = site?.siteKey;
    const siteInstanceKey = site?.id;

    console.log('viewEntity called:', { entity, siteKey, siteInstanceKey, selectedSite: site });

    if (!siteKey || !siteInstanceKey) {
      console.error('Missing siteKey or siteInstanceKey:', { siteKey, siteInstanceKey });
      return;
    }

    // Load entity detail summary and open modal
    this.siteDetailsService.getEntityDetailSummary(entity.entityCode || entity.id, siteKey, siteInstanceKey)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (entitySummary: EntityDetailSummary) => {
          this.selectedEntityData.set({
            id: entitySummary.id,
            name: entitySummary.name,
            entityCode: entitySummary.entityCode,
            totalEntities: entitySummary.totalEntities,
            totalResignedEntities: entitySummary.totalResignedEntities,
            totalInvoices: entitySummary.totalInvoices,
            totalShareClasses: entitySummary.totalShareClasses,
            totalBankAccounts: entitySummary.totalBankAccounts,
            totalInvestments: entitySummary.totalInvestments,
            totalAssets: entitySummary.totalAssets,
            totalLoans: entitySummary.totalLoans,
            totalDocuments: entitySummary.totalDocuments
          });
          this.isEntityDetailOpen.set(true);
        },
        error: (error) => {
          console.error('Error loading entity summary:', error);
        }
      });
  }

  openUserDetail(user: SiteUser): void {
    this.selectedUserData.set({
      id: user.id,
      name: user.name,
      displayName: user.displayName,
      email: user.email,
      initials: user.initials,
      avatarColor: user.avatarColor,
      joinedDate: user.joinedDate,
      status: user.status,
      termsAccepted: user.termsAccepted,
      authorizations: user.authorizations
    });
    this.isUserDetailOpen.set(true);
  }

  closeUserDetail(): void {
    this.isUserDetailOpen.set(false);
    this.selectedUserData.set(null);
  }

  onUserStatusChanged(event: { userId: string, status: number }): void {
    // Update the user status in the siteUsers array
    this.siteUsers.update(users => {
      const updated = [...users];
      const userIndex = updated.findIndex(user => user.id === event.userId);
      if (userIndex !== -1) {
        updated[userIndex] = { ...updated[userIndex], status: event.status };
        console.log(`Updated user ${event.userId} status to ${event.status}`);
      }
      return updated;
    });
    
    // Also update selectedUserData if it's the same user
    const currentUser = this.selectedUserData();
    if (currentUser?.id === event.userId) {
      this.selectedUserData.set({ ...currentUser, status: event.status });
    }
  }

  onUserPermissionsUpdated(updatedUserData: UserDetailData): void {
    this.selectedUserData.set(updatedUserData);
    
    if (updatedUserData.id) {
      this.siteUsers.update(users => {
        const updated = [...users];
        const userIndex = updated.findIndex(user => user.id === updatedUserData.id);
        if (userIndex !== -1) {
          updated[userIndex] = { ...updated[userIndex], authorizations: updatedUserData.authorizations };
        }
        return updated;
      });
    }
  }

  onUserDeleted(deletedUserId: string): void {
    this.siteUsers.update(users => users.filter(user => user.id !== deletedUserId));
    this.userCount.set(this.siteUsers().length);
    this.selectedUserData.set(null);
  }

  onUserTermsReset(updatedUserData: UserDetailData): void {
    this.selectedUserData.set(updatedUserData);
    
    if (updatedUserData.id) {
      this.siteUsers.update(users => {
        const updated = [...users];
        const userIndex = updated.findIndex(user => user.id === updatedUserData.id);
        if (userIndex !== -1) {
          updated[userIndex] = { ...updated[userIndex], termsAccepted: updatedUserData.termsAccepted };
        }
        return updated;
      });
    }
  }

  onUserUpdated(updatedUserData: UserDetailData): void {
    this.selectedUserData.set(updatedUserData);
    
    if (updatedUserData.id) {
      this.siteUsers.update(users => {
        const updated = [...users];
        const userIndex = updated.findIndex(user => user.id === updatedUserData.id);
        if (userIndex !== -1) {
          updated[userIndex] = { 
            ...updated[userIndex], 
            displayName: updatedUserData.displayName,
            name: updatedUserData.name
          };
        }
        return updated;
      });
    }
  }

  toggleSort(field: 'name' | 'userCount'): void {
    if (this.sortField() === field) {
      this.sortAscending.update(v => !v);
    } else {
      this.sortField.set(field);
      this.sortAscending.set(true);
    }
  }

  onSiteSearch(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.siteSearchTerm.set(value);
  }

  clearSiteSearch(): void {
    this.siteSearchTerm.set('');
  }

  onUserSearch(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.userSearchTerm.set(value);
  }

  clearUserSearch(): void {
    this.userSearchTerm.set('');
  }

  readonly isExportingSites = signal(false);

  exportSitesAsCsv(): void {
    // Defense-in-depth: Verify admin access (parallels backend [Authorize("Portal.Admin")])
    if (!this.isAdmin()) {
      return;
    }

    this.isExportingSites.set(true);

    this.siteDetailsService.getAllSiteUsers()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (allUsers: UserSiteAccess[]) => {
          const sorted = [...allUsers].sort((a, b) => {
            const siteCompare = (a.siteInstanceName ?? '').localeCompare(b.siteInstanceName ?? '');
            if (siteCompare !== 0) return siteCompare;
            return (a.user?.emailAddress ?? '').localeCompare(b.user?.emailAddress ?? '');
          });

          const header = 'Site Instance Name,Email,Name,Status,Tasks,Entities,Documents,Invoices,Messages,Shares,Related Parties,Requests,Banking,Assets,Investments,Information,Loans';
          const rows = sorted.map(usa => {
            const siteInstanceName = this.sanitizeCsvValue(usa.siteInstanceName ?? '');
            const email = this.sanitizeCsvValue(usa.user?.emailAddress ?? '');
            const displayName = this.sanitizeCsvValue(usa.user?.displayName ?? '');
            const status = this.getUserStatusText(usa.user?.status);
            const tasks = usa.tasksAuthorization ?? false;
            const entities = usa.entitiesAuthorization ?? false;
            const documents = usa.documentsAuthorization ?? false;
            const invoices = usa.invoicesAuthorization ?? false;
            const messages = usa.messagesAuthorization ?? false;
            const shares = usa.sharesAuthorization ?? false;
            const relatedParties = usa.relatedPartiesAuthorization ?? false;
            const requests = usa.requestsAuthorization ?? false;
            const banking = usa.bankingAuthorization ?? false;
            const assets = usa.assetsAuthorization ?? false;
            const investments = usa.investmentsAuthorization ?? false;
            const information = usa.informationAuthorization ?? false;
            const loans = usa.loansAuthorization ?? false;
            return `${siteInstanceName},${email},${displayName},${status},${tasks},${entities},${documents},${invoices},${messages},${shares},${relatedParties},${requests},${banking},${assets},${investments},${information},${loans}`;
          });

          const csvContent = [header, ...rows].join('\r\n');
          const BOM = '\uFEFF';
          const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8' });
          const timestamp = new Date().toISOString().replaceAll(/[:.]/g, '-').slice(0, -5) + '-utc';
          saveAs(blob, `site-instances-${timestamp}.csv`);
          this.isExportingSites.set(false);
        },
        error: (error) => {
          console.error('Error exporting sites CSV:', error);
          this.isExportingSites.set(false);
          alert('Export failed - please try again or contact support.');
        }
      });
  }

  exportUsersAsCsv(): void {
    // Defense-in-depth: Verify admin access (parallels backend [Authorize("Portal.Admin")])
    if (!this.isAdmin()) {
      return;
    }

    const users = this.filteredSiteUsers();
    if (users.length === 0) {
      return;
    }

    const siteName = this.selectedSite()?.name ?? 'unknown';
    const sortedUsers = [...users].sort((a, b) => a.email.localeCompare(b.email));
    const header = 'Email,Name,Status,Tasks,Entities,Documents,Invoices,Messages,Shares,Related Parties,Requests,Banking,Assets,Investments,Information,Loans';
    const rows = sortedUsers.map(user => {
      const email = this.sanitizeCsvValue(user.email);
      const name = this.sanitizeCsvValue(user.displayName ?? '');
      const status = this.getUserStatusText(user.status);
      const auth = user.authorizations;
      const tasks = auth?.tasks ?? false;
      const entities = auth?.entities ?? false;
      const documents = auth?.documents ?? false;
      const invoices = auth?.invoices ?? false;
      const messages = auth?.messages ?? false;
      const shares = auth?.shares ?? false;
      const relatedParties = auth?.relatedParties ?? false;
      const requests = auth?.requests ?? false;
      const banking = auth?.banking ?? false;
      const assets = auth?.assets ?? false;
      const investments = auth?.investments ?? false;
      const information = auth?.information ?? false;
      const loans = auth?.loans ?? false;
      return `${email},${name},${status},${tasks},${entities},${documents},${invoices},${messages},${shares},${relatedParties},${requests},${banking},${assets},${investments},${information},${loans}`;
    });

    const csvContent = [header, ...rows].join('\r\n');
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8' });
    const fileName = `users-${siteName.replaceAll(/[^a-zA-Z0-9]/g, '-')}.csv`;
    saveAs(blob, fileName);
  }

  closeEntityDetail(): void {
    this.isEntityDetailOpen.set(false);
    this.selectedEntityData.set(null);
  }

  onModalKeyDown(event: KeyboardEvent): void {
    // Handle Escape key to close modal
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeDetailPanel();
    }
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Escape') {
      return;
    }

    if (this.isAddUserModalOpen()) {
      this.closeAddUserModal();
    } else if (this.isEntityDetailOpen()) {
      this.closeEntityDetail();
    } else if (this.isUserDetailOpen()) {
      this.closeUserDetail();
    } else if (this.isDetailPanelOpen()) {
      this.closeDetailPanel();
    }
  }

  getUserStatusText(status?: number): string {
    return Utils.getUserStatusText(status);
  }

  private sanitizeCsvValue(value: string): string {
    // Prevent CSV formula injection by prefixing values starting with =, +, -, @ with a single quote
    const formulaTriggers = ['=', '+', '-', '@'];
    const needsSanitization = formulaTriggers.some(trigger => value.startsWith(trigger));
    
    if (needsSanitization) {
      value = `'${value}`;
    }
    
    // Wrap in quotes and escape internal quotes if value contains comma or quote
    if (value.includes(',') || value.includes('"')) {
      value = `"${value.replaceAll('"', '""')}"`;
    }
    
    return value;
  }

  private loadSiteData(): void {
    this.error.set(null);
    const tenantId = this.userContextService.getTenantId();

    if (!tenantId) {
      this.error.set('Unable to load site data: No tenant context available.');
      return;
    }

    this.sitesService.getInstances()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.siteData.set(data);
        },
        error: (err) => {
          console.error('Error loading site data:', err);
          this.error.set('Failed to load site data. Please try again later.');
        }
      });
  }
}
